/**
 * Frontend mirror of functions-forms/src/kb-assistant/compliancePolicy.ts.
 * Keep both in sync — Cloud Functions cannot import from src/.
 */

/**
 * Hard rules, overridable settings, and the override audit record.
 * Defaults are the safe path. Overrides are evidence, not a legal shield.
 */

 













export const HARD_RULES = [
  {
    id: 'neverClaimHuman',
    statement: 'The operator never claims to be a human.',
    why: 'Identity honesty is product-wide, not a geography toggle. "Are you a real person?" is answered truthfully by construction.',
  },
  {
    id: 'neverInventFigures',
    statement: 'The operator never states a price, fee, or figure that is not in the knowledge base.',
    why: 'This is what keeps a number the AI quotes from being treated as a binding offer — and what failed on Aug 23.',
  },
  {
    id: 'neverGuaranteeOutcomes',
    statement: 'The operator never guarantees rankings, outcomes, or results.',
    why: 'Promised results are a well-known FTC and platform-policy problem. The AI cannot be configured to make them.',
  },
  {
    id: 'neverInventProof',
    statement: 'The operator never invents testimonials, client names, results, or statistics.',
    why: 'FTC endorsement rules apply to AI-generated claims the same way they apply to human ones.',
  },
  {
    id: 'neverCollectClinicalUnlessConfigured',
    statement: 'The operator never collects clinical information unless the medical path is explicitly configured.',
    why: 'Form, chat, and email are not HIPAA-compliant channels. Collecting symptoms or medications is a breach risk.',
  },
];

 
















export const OVERRIDABLE_SETTINGS = [
  {
    id: 'aiAssistedNotice',
    label: 'AI-assisted notice',
    defaultValue: 'on',
    reason: 'A short notice that replies are AI-assisted keeps identity honest without a banner or a geo-gated disclosure.',
    risk: 'inline',
  },
  {
    id: 'pricingPolicyMode',
    label: 'Pricing mode',
    defaultValue: 'kb',
    reason: 'This is what keeps a price the AI quotes from being treated as a binding offer. The invention ban still applies in every mode.',
    risk: 'inline',
  },
  {
    id: 'responseTimePromise',
    label: 'Response-time promise',
    defaultValue: 'as_stated',
    reason: 'The operator only promises the response time you stated. Inventing "we reply in 5 minutes" is how you lose the next lead.',
    risk: 'inline',
  },
  {
    id: 'licenceDisplay',
    label: 'Licence-number display',
    defaultValue: 'show_if_provided',
    reason: 'Many states require contractor or professional licence disclosure. The AI only repeats numbers you supplied.',
    risk: 'inline',
  },
  {
    id: 'goalAggressiveness',
    label: 'How hard the operator pushes the goal',
    defaultValue: 'qualify_first',
    reason: 'Qualify first, then move toward the goal. Pushing a booking before the lead is a fit creates cancels and complaints.',
    risk: 'inline',
  },
];

/** The only overrides that require a full-stop warning and typed acknowledgment. */
 

export const HIGH_RISK_OVERRIDE_IDS = [
  'clinicalDataPath',
  'legalAdvicePath',
  'guaranteedResults',
];










export const HIGH_RISK_WARNINGS = {
  clinicalDataPath: {
    id: 'clinicalDataPath',
    version: 'kb-asst-warn-clinical-v1',
    title: 'Clinical data on this channel is a breach risk',
    body:
      'Form, chat, and email are not HIPAA-compliant unless you have a BAA in place. If the operator invites or stores symptoms, conditions, or medications, you have a breach problem and we are the processor. The safe default is never collect clinical information and route to the office. Turning this path on does not make the channel compliant. An acknowledgment is evidence you were informed, not a legal shield — indemnification lives in the terms of service.',
    acknowledgmentPrompt: 'Type I UNDERSTAND to record that you accept this risk.',
    requiredPhrase: 'I UNDERSTAND',
  },
  legalAdvicePath: {
    id: 'legalAdvicePath',
    version: 'kb-asst-warn-legal-v1',
    title: 'This channel must not give legal advice',
    body:
      'If the operator answers legal questions, you risk unauthorized practice of law and an implied attorney-client relationship. The safe default is intake only: collect facts the firm named, never advise, and hand off. Turning this path on does not make the operator your associate. An acknowledgment is evidence you were informed, not a legal shield.',
    acknowledgmentPrompt: 'Type I UNDERSTAND to record that you accept this risk.',
    requiredPhrase: 'I UNDERSTAND',
  },
  guaranteedResults: {
    id: 'guaranteedResults',
    version: 'kb-asst-warn-results-v1',
    title: 'Guaranteed results are a claim the AI must not make',
    body:
      'Promised rankings, outcomes, or results are a well-known FTC and platform-policy problem. The operator must never guarantee them. If you override this, you are asking the product to make a claim we cannot stand behind. An acknowledgment is evidence you were informed, not a legal shield.',
    acknowledgmentPrompt: 'Type I UNDERSTAND to record that you accept this risk.',
    requiredPhrase: 'I UNDERSTAND',
  },
};












export function warningVersionForSetting(setting) {
  if (setting in HIGH_RISK_WARNINGS) {
    return HIGH_RISK_WARNINGS[setting ].version;
  }
  return `kb-asst-inline-${setting}-v1`;
}

export function requiresTypedAcknowledgment(setting) {
  return HIGH_RISK_OVERRIDE_IDS.includes(setting );
}

export function acknowledgmentMatches(setting, typed) {
  if (!requiresTypedAcknowledgment(setting)) return true;
  const required = HIGH_RISK_WARNINGS[setting ].requiredPhrase;
  return typed.trim() === required;
}

/**
 * Build the audit row for an override. Callers must pass the version of the
 * warning text that was actually on screen.
 */
export function buildOverrideRecord(input







) {
  const version = (input.warningTextVersion || '').trim();
  if (!version) {
    throw new Error('warningTextVersion is required — without it the record proves nothing');
  }
  return {
    workspaceId: input.workspaceId,
    userId: input.userId,
    setting: input.setting,
    previousValue: input.previousValue,
    newValue: input.newValue,
    timestamp: input.timestamp || new Date().toISOString(),
    warningTextVersion: version,
  };
}

export function isHardRuleId(id) {
  return HARD_RULES.some((rule) => rule.id === id);
}
